angular.module('dentalLinksServices')

.factory('UploadService', ['FileUploader', 'Notification', 'API_ENDPOINT', 'ProgressIndicator', 'Logger', '$timeout',
	function (FileUploader, Notification, API_ENDPOINT, ProgressIndicator, Logger, $timeout) {
		// --- Attachment size limits ---------------------------------------------
		// MAX_ATTACHMENT_FILE_SIZE caps a single file; MAX_REFERRAL_ATTACHMENTS_SIZE
		// caps the combined size of all attachments on one referral.
		//
		// IMPORTANT: keep these in sync with the matching backend constants in
		// backend/app/models/attachment.rb:
		//   MAX_ATTACHMENT_FILE_SIZE      <-> Attachment::MAX_FILE_SIZE
		//   MAX_REFERRAL_ATTACHMENTS_SIZE <-> Attachment::MAX_TOTAL_SIZE_PER_REFERRAL
		var MAX_ATTACHMENT_FILE_SIZE = 50 * 1024 * 1024;       // 50 MB per file
		var MAX_REFERRAL_ATTACHMENTS_SIZE = 250 * 1024 * 1024; // 250 MB total per referral

		// --- Disallowed attachment types ----------------------------------------
		// Files whose browser MIME type or filename extension matches these lists
		// are rejected the moment they are queued, so the user is warned immediately
		// instead of discovering the rejection only after the upload round-trip on
		// "Sign & Send" (DCL-399). The browser-reported MIME type is unreliable for
		// executables (often empty or vendor-specific), so we also reject by
		// extension.
		//
		// IMPORTANT: keep these in sync with the content-type blacklist in
		// backend/app/models/attachment.rb (validates_attachment :attach,
		// content_type: {not: [...]}).
		var DISALLOWED_FILE_MIME_TYPES = [
			'application/zip', 'application/x-msdownload', 'application/x-ms-dos-executable',
			'application/x-dosexec',
			'application/x-msdos-program', 'application/x-msdos-windows', 'application/x-download',
			'application/bat', 'application/x-bat', 'application/com', 'application/x-com',
			'application/exe', 'application/x-exe', 'application/x-winexe', 'application/x-winhlp',
			'application/x-winhelp', 'application/x-javascript', 'application/hta',
			'application/x-ms-shortcut', 'vms/exe'
		];
		// Extensions corresponding to the blacklisted MIME types above (executables,
		// scripts, shortcuts, Windows help files and zip archives).
		var DISALLOWED_FILE_EXTENSIONS = [
			'exe', 'dll', 'com', 'bat', 'cmd', 'msi', 'scr', 'hta', 'hlp', 'lnk', 'js', 'zip'
		];

		// Filters owned by this service. A file they reject is reported by the
		// deferred rejection report below, so the generic onWhenAddingFileFailed
		// message must not fire on top of it.
		var OWN_FILTER_NAMES = ['fileTypeFilter', 'fileSizeFilter'];

		// Bytes held by an attachment, whichever shape the caller has: a persisted
		// attachment (attach_file_size), an uploader queue item (file.size), a
		// FileLikeObject (size), or a plain byte count.
		function sizeOf(attachment) {
			if (typeof attachment === 'number') {
				return attachment;
			}
			if (!attachment) {
				return 0;
			}
			var bytes = attachment.attach_file_size;
			if (typeof bytes === 'undefined' && attachment.file) {
				bytes = attachment.file.size;
			}
			if (typeof bytes === 'undefined') {
				bytes = attachment.size;
			}
			bytes = Number(bytes);
			return isFinite(bytes) && bytes > 0 ? bytes : 0;
		}

		// One message covering everything a single selection turned away, so the
		// user learns WHICH files were rejected and why (DCL-230). The Notification
		// service holds a single message slot, so notifying per file would leave
		// only the last one standing.
		//
		// A lone rejection with a caller-supplied message keeps that wording
		// verbatim — the branding-logo uploader relies on it.
		function buildRejectionMessage(rejections) {
			if (rejections.length === 1) {
				var only = rejections[0];
				return only.message || (only.name + ' was not added — ' + only.reason + '.');
			}

			// Group by reason, keeping the order the files were selected in.
			var reasons = [];
			var namesByReason = {};
			angular.forEach(rejections, function (rejection) {
				if (!namesByReason[rejection.reason]) {
					namesByReason[rejection.reason] = [];
					reasons.push(rejection.reason);
				}
				namesByReason[rejection.reason].push(rejection.name);
			});

			var parts = reasons.map(function (reason) {
				return namesByReason[reason].join(', ') + ' (' + reason + ')';
			});
			return rejections.length + ' files were not added — ' + parts.join('; ') + '.';
		}

		function createUploader(config) {
			config = config || {};
			var scope = config.scope;
			var uploader = new FileUploader({
				scope: scope,
				url: config.url || (API_ENDPOINT + '/attachment/upload'),
				alias: config.alias || 'attach',
				formData: (config.formData || [{filename: ''}]).slice(0),
				withCredentials: true  // Send cookies (including auth cookie) with upload requests
			});

			var eachLimit = config.eachFileSizeLimit || MAX_ATTACHMENT_FILE_SIZE;
			var totalLimit = config.totalFileSizeLimit || MAX_REFERRAL_ATTACHMENTS_SIZE;

			if (typeof scope.total_size === 'undefined') {
				scope.total_size = 0;
			}

			// --- Deferred rejection report ---------------------------------------
			// The uploader applies the filters file by file, so a multi-file
			// selection can produce several rejections in one pass. Collect them and
			// report once on the next tick (DCL-230).
			var rejections = [];
			var reportScheduled = false;

			function rejectFile(item, reason, message) {
				rejections.push({name: item.name, reason: reason, message: message});
				if (!reportScheduled) {
					reportScheduled = true;
					$timeout(function () {
						reportScheduled = false;
						var reported = rejections.splice(0, rejections.length);
						if (reported.length) {
							Notification.error(buildRejectionMessage(reported));
						}
					}, 0);
				}
				return false;
			}

			// Frees the bytes an attachment was holding once it is deleted or
			// removed from the queue. Without this the running total is a
			// high-water mark and eventually rejects uploads the referral has room
			// for (DCL-230).
			uploader.releaseSize = function (attachment) {
				scope.total_size = Math.max(0, (scope.total_size || 0) - sizeOf(attachment));
				return scope.total_size;
			};

			// Filters
			// Reject blacklisted file types up-front, before the file is queued, so
			// the user is warned immediately rather than after a failed upload
			// (DCL-399). Runs before fileSizeFilter so a rejected file is not counted
			// toward scope.total_size.
			uploader.filters.push({
				name: 'fileTypeFilter',
				fn: function (item) {
					var type = (item.type || '').toLowerCase();
					var name = (item.name || '').toLowerCase();
					var dot = name.lastIndexOf('.');
					var ext = dot >= 0 ? name.slice(dot + 1) : '';
					if (DISALLOWED_FILE_MIME_TYPES.indexOf(type) !== -1 ||
						(ext && DISALLOWED_FILE_EXTENSIONS.indexOf(ext) !== -1)) {
						return rejectFile(item, 'this file type is not allowed as an attachment',
							config.messages && config.messages.disallowedType);
					}
					return true;
				}
			});

			uploader.filters.push({
				name: 'fileSizeFilter',
				fn: function (item) {
					if (item.size > eachLimit) {
						return rejectFile(item, 'larger than ' + parseInt(eachLimit/1048576) + ' MB',
							config.messages && config.messages.eachTooLarge);
					}
					if ((scope.total_size + item.size) > totalLimit) {
						return rejectFile(item, 'over the ' + parseInt(totalLimit/1048576) + ' MB total for this referral',
							config.messages && config.messages.totalTooLarge);
					}
					scope.total_size = scope.total_size + item.size;
					return true;
				}
			});

			// Handlers
			uploader.onAfterAddingFile = function(item) {
				item.metadata = {last_modified: item.file.lastModifiedDate};
				item.formData.push(item.metadata);
				if (config.immediateUpload) {
					ProgressIndicator.start();
					item.upload();
				}
			};

			uploader.onWhenAddingFileFailed = function(item, filter) {
				// Our own filters already queued a rejection carrying the file name
				// and the reason; a generic message on top would only overwrite it.
				if (filter && OWN_FILTER_NAMES.indexOf(filter.name) !== -1) {
					return;
				}
				var msg = (config.messages && config.messages.addFail);
				if (msg) {
					Notification.error(msg);
				} else {
					if (!Notification.get().message || Notification.get().type != 'danger') {
						Notification.error('Adding file failed. Please try again later.');
					}
				}
			};

			uploader.onAfterAddingAll = function(items) {
				if (typeof config.onAfterAddingAll === 'function') {
					config.onAfterAddingAll(items);
				}
			};

			uploader.onBeforeUploadItem = function() {
				if (config.startProgressOnBeforeUpload) {
					ProgressIndicator.start();
				}
			};

			uploader.onProgressItem = function(item, progress) {
				if (typeof config.onProgressItem === 'function') {
					config.onProgressItem(item, progress);
				}
			};

			uploader.onSuccessItem = function(item, response, status, headers) {
				if (typeof config.onSuccessItem === 'function') {
					config.onSuccessItem(item, response, status, headers);
				} else {
					item.downloadUrl = response.attach_file_name;
				}
			};

			uploader.onCancelItem = function(item, response, status, headers) {
				Notification.info((config.messages && config.messages.cancelInfo) || 'Attachment upload was cancelled.');
				if (typeof config.onCancelItem === 'function') {
					config.onCancelItem(item, response, status, headers);
				}
			};

			uploader.onErrorItem = function(item, response, status, headers) {
				Logger.error('Error', response, item, status);
				if (typeof config.onErrorItem === 'function') {
					config.onErrorItem(item, response, status, headers);
				} else {
					ProgressIndicator.finish();
					// Prefer a server-supplied friendly message (e.g. the DCL-215
					// monthly upload-cap or per-referral size-limit rejection, both
					// { message: "..." }), then Paperclip field errors, then a generic.
					var errorMessage = response && response.message ? response.message : (response && response.file ? response.file[0] : (response && response.attach ? 'Sorry this file type is not allowed as an attachment.' : 'An error occurred during attachment upload. Please try again later.'));
					Notification.error(errorMessage, {dontHideOnRequest: true});
				}
			};

			uploader.onCompleteItem = function(item, response, status, headers) {
				if (typeof config.onCompleteItem === 'function') {
					config.onCompleteItem(item, response, status, headers);
				}
			};

			uploader.onProgressAll = function(progress) {
				ProgressIndicator.set(progress);
			};

			uploader.onCompleteAll = function() {
				ProgressIndicator.finish();
				if (typeof config.onCompleteAll === 'function') {
					config.onCompleteAll();
				}
			};

			return uploader;
		}

		return {
			createUploader: createUploader
		};
	}
]);